import React, { FC, useEffect } from 'react';

import clsx from 'clsx';

import { ButtonChip, LinkChip } from '@domains/chips';

import { isValueSet } from '@utils/guards';

import { useChipsSlider } from '@hooks/use-chips-slider';

import { Container } from '@components/common/Container/Container';
import { Link } from '@components/common/Link/Link';
import { SliderNavigation } from '@components/common/SliderNavigation/SliderNavigation';

import styles from './Chips.module.scss';

export enum ChipsKind {
    Links,
    Buttons,
}

export type ChipsProps = (
    | {
          data: LinkChip[];
          kind: ChipsKind.Links;
      }
    | {
          data: ButtonChip[];
          kind: ChipsKind.Buttons;
      }
) & {
    activeChipId: number | string;
    className?: string;
};

export const Chips: FC<ChipsProps> = (props) => {
    const { kind, data, activeChipId, className } = props;

    const { isAtStart, isAtEnd, scrollPrev, scrollNext, viewportRef, emblaApi } = useChipsSlider();

    /*
     * for ChipsKind.Links
     * Scrolling to active chip to view after page loading
     * */
    useEffect(() => {
        if (!isValueSet(emblaApi)) return;

        if (kind === ChipsKind.Links) {
            const activeChipIndex = (data as LinkChip[]).findIndex(
                (chipData) => chipData.id === activeChipId,
            );

            if (activeChipIndex === -1) return;

            emblaApi.reInit({
                startIndex: activeChipIndex,
            });
        }
    }, [activeChipId, data, emblaApi, kind]);

    return (
        <div className={clsx(styles.root, className)}>
            <Container classes={{ root: styles.container }}>
                <div className={styles.slider}>
                    <div ref={viewportRef} className={styles.viewport}>
                        <ul className={styles.chipList}>
                            {kind === ChipsKind.Links &&
                                (data as LinkChip[]).map(({ id, url, caption }) => (
                                    <li key={id} className={styles.chipListItem}>
                                        <Link
                                            route={url}
                                            className={clsx(
                                                styles.chip,
                                                id === activeChipId && styles.isActive,
                                            )}
                                        >
                                            {caption}
                                        </Link>
                                    </li>
                                ))}
                            {kind === ChipsKind.Buttons &&
                                (data as ButtonChip[]).map(({ id, caption, onClick }) => (
                                    <li key={id} className={styles.chipListItem}>
                                        <button
                                            type="button"
                                            className={clsx(
                                                styles.chip,
                                                id === activeChipId && styles.isActive,
                                            )}
                                            onClick={onClick}
                                        >
                                            {caption}
                                        </button>
                                    </li>
                                ))}
                        </ul>

                        <SliderNavigation
                            onPrev={scrollPrev}
                            onNext={scrollNext}
                            variation="circleWithChevron"
                            classes={{
                                button: styles.navigationButton,
                                prev: isAtStart ? styles.isHidden : undefined,
                                next: isAtEnd ? styles.isHidden : undefined,
                            }}
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
};
